import { useState, useMemo, useRef, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Tooltip, Legend, Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SearchInput } from '@/shared/ui/SearchInput';
import { paramTypeMeta, typography, semantic, radius } from '@/shared/theme/tokens';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const SIM_BRAND     = semantic.blue;
const successColor  = (isDark) => isDark ? '#4ade80' : semantic.successGreen;
const warnColor     = (isDark) => isDark ? '#fbbf24' : '#d97706';
const dangerColor   = (isDark) => isDark ? '#f87171' : semantic.error;
const BASELINE_CLR  = '#94a3b8'; // slate-400 — neutral grey for "no-change" line

function fmtVal(v) {
  if (v == null) return '—';
  return typeof v === 'number' ? (Math.abs(v) >= 100 ? v.toFixed(1) : v.toFixed(2)) : String(v);
}

// ── Simulation loader (AUT-188) ───────────────────────────────────────────────────
const SIM_CHECKLIST = [
  'Reading current state…',
  'Applying your adjustments…',
  'Mapping causal dependencies…',
  'Simulating downstream effects…',
  'Computing predicted outcomes…',
  'Finalising predictions…',
];

// Progress: reaches ~99% in 50s (500ms per 1%), holds at 99, jumps to 100 when done.
function useSimProgress(active) {
  const [pct, setPct] = useState(0);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    if (!active) { setPct(0); setCompleting(false); return undefined; }
    const t = setInterval(() => setPct((p) => (p < 99 ? p + 1 : 99)), 500);
    return () => clearInterval(t);
  }, [active]);

  // When result arrives: animate to 100 then signal complete
  useEffect(() => {
    if (!active && pct > 0 && !completing) {
      setCompleting(true);
      setPct(100);
    }
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  return { pct, completing };
}

// Each checklist item ticks when progress crosses its threshold.
// 6 items spread across 0-90% so last item ticks before bar reaches 99.
const CHECKLIST_THRESHOLDS = [10, 25, 40, 55, 70, 85];

function SimLoader({ pct, showingResult, isDark, c }) {
  const brand = SIM_BRAND;
  const trackBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(30,67,150,0.1)';

  // All items tick when showingResult (pct=100), otherwise tick by threshold
  const tickedCount = showingResult ? SIM_CHECKLIST.length : CHECKLIST_THRESHOLDS.filter((t) => pct >= t).length;
  const currentIdx = showingResult ? -1 : tickedCount;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      height: '100%', minHeight: 300, padding: '0 48px',
    }}>
      {/* Progress bar */}
      <div style={{ width: '100%', maxWidth: 360, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.bold, color: brand }}>Running simulation</span>
          <span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.bold, color: brand, fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
        </div>
        <div style={{ height: 6, borderRadius: radius.sm, background: trackBg, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: radius.sm, background: brand,
            width: `${pct}%`, transition: 'width 1.8s ease',
          }} />
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      {/* Checklist */}
      <div style={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {SIM_CHECKLIST.map((label, i) => {
          const done = i < tickedCount;
          const running = i === currentIdx;
          return (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: done || running ? 1 : 0.35 }}>
              <span className="material-symbols-outlined" style={{
                fontSize: 16, flexShrink: 0, fontVariationSettings: "'FILL' 1",
                color: done ? semantic.successGreen : running ? brand : c.textSubtle,
                animation: running ? 'spin 1s linear infinite' : 'none',
              }}>
                {done ? 'check_circle' : running ? 'progress_activity' : 'radio_button_unchecked'}
              </span>
              <span style={{ fontSize: typography.fontSize.md, fontWeight: done || running ? 600 : 400, color: done || running ? c.textPrimary : c.textSubtle }}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function inOptimal(v, lo, hi) {
  if (v == null) return false;
  return (lo == null || v >= lo) && (hi == null || v <= hi);
}

// ── Outcome variable picker ───────────────────────────────────────────────────────
function OutcomePicker({ allOutcomes, activeKeys, setActiveKeys, c, isDark }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => { if (!open) setSearch(''); }, [open]);

  const toggle = (key) => setActiveKeys((prev) =>
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
  );

  const filtered = useMemo(() => {
    if (!search) return allOutcomes;
    const q = search.toLowerCase();
    return allOutcomes.filter((o) => o.displayName.toLowerCase().includes(q) || o.dataKey.toLowerCase().includes(q));
  }, [allOutcomes, search]);

  const targets   = filtered.filter((o) => o.type === 'target');
  const monitored = filtered.filter((o) => o.type === 'monitored');

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '5px 12px', borderRadius: radius.md, cursor: 'pointer',
          background: c.bgMuted, border: `1px solid ${c.border}`,
          fontFamily: typography.fontFamily.body,
          fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: c.textLabel,
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 14, color: c.textSubtle }}>tune</span>
        Variables
        <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, background: SIM_BRAND, color: '#fff', borderRadius: radius.md, padding: '1px 6px', marginLeft: 2 }}>
          {activeKeys.filter((k) => allOutcomes.some((o) => o.dataKey === k)).length}
        </span>
        <span className="material-symbols-outlined" style={{ fontSize: 14, color: c.textSubtle, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
          expand_more
        </span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 100,
          background: c.dropdownBg, border: `1px solid ${c.border}`, borderRadius: radius.xl,
          boxShadow: c.dropdownShadow, minWidth: 260, maxHeight: 380, display: 'flex', flexDirection: 'column',
        }}>
          {/* Search inside dropdown */}
          <div style={{ padding: '8px 10px 4px', flexShrink: 0 }}>
            <SearchInput
              value={search}
              onChange={setSearch}
              onClear={() => setSearch('')}
              placeholder="Search variables…"
              size="sm"
              autoFocus
            />
          </div>

          <div style={{ overflowY: 'auto', flex: 1, padding: '4px 0 8px' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '16px 12px', fontSize: typography.fontSize.base, color: c.textSubtle, textAlign: 'center' }}>No variables match</div>
            ) : (
              [{ label: 'Targets', items: targets }, { label: 'Monitored', items: monitored }].map(({ label, items }) =>
                items.length > 0 && (
                  <div key={label}>
                    <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase', padding: '4px 12px 2px' }}>
                      {label}
                    </div>
                    {items.map((o) => {
                      const active = activeKeys.includes(o.dataKey);
                      const meta = paramTypeMeta[o.type] ?? {};
                      return (
                        <div
                          key={o.dataKey}
                          onClick={() => toggle(o.dataKey)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
                            cursor: 'pointer',
                            background: active ? (isDark ? 'rgba(37,99,235,0.12)' : 'rgba(37,99,235,0.06)') : 'transparent',
                          }}
                        >
                          <span className="material-symbols-outlined" style={{
                            fontSize: 15, color: active ? SIM_BRAND : c.textSubtle,
                            fontVariationSettings: active ? "'FILL' 1" : "'FILL' 0",
                          }}>{active ? 'check_box' : 'check_box_outline_blank'}</span>
                          <span style={{ flex: 1, fontSize: typography.fontSize.base, color: c.textPrimary, fontWeight: active ? 600 : 400 }}>
                            {o.displayName}
                          </span>
                          <span style={{ fontSize: typography.fontSize.xs, padding: '1px 5px', borderRadius: radius.sm, background: meta.bg, color: meta.text, fontWeight: typography.fontWeight.semibold }}>
                            {o.unit}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Single outcome card ───────────────────────────────────────────────────────────
function OutcomeCard({ outcome, hasRun, isDark, c }) {
  const {
    currentValue: cur, predictedValue: pred,
    optimumLow: lo, optimumHigh: hi,
    // BE sends timeSeriesValues: [{t, baseline, scenario}]
    timeSeriesValues = [],
    stabilizeAfter,
  } = outcome;

  // Extract parallel arrays from the unified series
  const labels       = timeSeriesValues.length ? timeSeriesValues.map((p) => p.t) : ['Now'];
  const scenarioData = timeSeriesValues.length ? timeSeriesValues.map((p) => p.scenario ?? p.value ?? cur) : [cur];
  const baselineData = timeSeriesValues.length && timeSeriesValues[0]?.baseline != null
    ? timeSeriesValues.map((p) => p.baseline)
    : null;

  const hasPred = pred != null && scenarioData.length > 0;
  const delta   = hasPred ? pred - cur : null;
  const meta    = paramTypeMeta[outcome.type] ?? {};

  const inOptCur  = inOptimal(cur, lo, hi);
  const inOptPred = inOptimal(pred, lo, hi);

  const green = successColor(isDark);
  const red   = dangerColor(isDark);
  const amber = warnColor(isDark);

  const scenarioColor = !hasPred ? SIM_BRAND : inOptPred ? green : red;

  const datasets = [];

  if (hasPred && baselineData) {
    datasets.push({
      label: 'Baseline',
      data: baselineData,
      borderColor: BASELINE_CLR,
      backgroundColor: 'transparent',
      borderWidth: 1.5,
      borderDash: [4, 3],
      pointRadius: 0,
      pointHoverRadius: 4,
      tension: 0.35,
      fill: false,
      order: 2,
    });
  }

  datasets.push({
    label: hasPred ? 'Scenario' : outcome.displayName,
    data: scenarioData,
    borderColor: scenarioColor,
    backgroundColor: 'transparent',
    borderWidth: 2,
    pointRadius: (ctx) => {
      const i = ctx.dataIndex;
      return (i === 0 || i === scenarioData.length - 1) ? 4 : 0;
    },
    pointHoverRadius: 5,
    pointBackgroundColor: scenarioColor,
    tension: 0.35,
    fill: false,
    order: 1,
  });

  const allVals = [cur, pred, lo, hi, ...scenarioData, ...(baselineData ?? [])].filter((v) => v != null);
  const minV = allVals.length ? Math.min(...allVals) : 0;
  const maxV = allVals.length ? Math.max(...allVals) : 1;
  const pad  = (maxV - minV) * 0.22 || Math.abs(maxV) * 0.12 || 1;

  const chartData = { labels, datasets };

  const opts = {
    responsive: true, maintainAspectRatio: false, animation: { duration: 320 },
    interaction: {
      mode: 'index',
      intersect: false,  // tooltip fires across full x width — no precise aim needed
    },
    plugins: {
      legend: {
        display: hasPred && !!baselineData,
        position: 'top',
        align: 'end',
        labels: {
          boxWidth: 16, boxHeight: 2, padding: 6,
          color: c.textSubtle, font: { size: 9 },
          usePointStyle: true, pointStyleWidth: 14,
        },
      },
      tooltip: {
        backgroundColor: c.dropdownBg,
        titleColor: c.textSubtle,
        bodyColor: c.textPrimary,
        borderColor: c.border,
        borderWidth: 1,
        padding: 10,
        callbacks: {
          title: (items) => items[0]?.label ?? '',
          label: (ctx) => ` ${ctx.dataset.label}: ${fmtVal(ctx.parsed.y)} ${outcome.unit ?? ''}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: c.textSubtle, font: { size: 9 }, maxRotation: 0 } },
      y: {
        min: minV - pad, max: maxV + pad,
        grid: { color: c.border },
        ticks: { color: c.textSubtle, font: { size: 9 } },
      },
    },
  };

  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius.xl,
      padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.bold, color: c.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
            {outcome.displayName}
          </div>
          {hasPred && delta != null && (
            <span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.bold, flexShrink: 0, color: delta > 0 ? green : delta < 0 ? amber : c.textSubtle }}>
              {delta > 0 ? '+' : ''}{fmtVal(delta)} {outcome.unit}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {/* Neutral type chip so orange stays unique to off-range values (AUT-192) */}
          <span style={{ fontSize: typography.fontSize.xs, padding: '1px 6px', borderRadius: radius.sm, background: c.bgMuted, color: c.textSubtle, border: `1px solid ${c.border}`, fontWeight: typography.fontWeight.semibold }}>{meta.label}</span>
          {hasPred && stabilizeAfter != null && (
            <span style={{
              fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, padding: '1px 6px', borderRadius: radius.sm,
              background: isDark ? 'rgba(37,99,235,0.15)' : 'rgba(37,99,235,0.08)',
              color: isDark ? '#93c5fd' : semantic.brandNavyMid,
            }}>~{stabilizeAfter}h to stabilize</span>
          )}
        </div>
      </div>

      {/* Current / Predicted values */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div>
          <div style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.label }}>Current</div>
          <div style={{ fontSize: typography.fontSize['4xl'], fontWeight: typography.fontWeight.extrabold, fontVariantNumeric: 'tabular-nums', color: inOptCur ? green : red }}>
            {fmtVal(cur)}
            <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.normal, color: c.textSubtle, marginLeft: 4 }}>{outcome.unit}</span>
          </div>
        </div>
        {hasPred && (
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.label }}>Predicted</div>
            <div style={{ fontSize: typography.fontSize['4xl'], fontWeight: typography.fontWeight.extrabold, fontVariantNumeric: 'tabular-nums', color: inOptPred ? green : red }}>
              {fmtVal(pred)}
              <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.normal, color: c.textSubtle, marginLeft: 4 }}>{outcome.unit}</span>
            </div>
          </div>
        )}
      </div>

      {/* Chart */}
      <div style={{ height: 120, position: 'relative' }}>
        {hasPred ? (
          <Line data={chartData} options={opts} />
        ) : (
          <div style={{
            height: '100%', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 4,
            borderRadius: radius.md, border: `1px dashed ${c.border}`,
            background: c.bgMuted,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 22, color: c.textSubtle, opacity: 0.5 }}>
              {hasRun ? 'bar_chart_off' : 'show_chart'}
            </span>
            <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>
              {hasRun ? 'No prediction found' : 'Run simulation to see prediction'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Loading skeleton for outcome cards ────────────────────────────────────────────
function OutcomeCardSkeleton({ c }) {
  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius.xl,
      padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        .sim-shimmer {
          background: linear-gradient(90deg, var(--sk-base) 25%, var(--sk-shine) 50%, var(--sk-base) 75%);
          background-size: 800px 100%;
          animation: shimmer 1.4s ease-in-out infinite;
          border-radius: 6px;
        }
      `}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div className="sim-shimmer" style={{ height: 13, width: '55%', '--sk-base': c.bgMuted, '--sk-shine': c.bgHover }} />
        <div className="sim-shimmer" style={{ height: 9, width: '35%', '--sk-base': c.bgMuted, '--sk-shine': c.bgHover }} />
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        <div>
          <div className="sim-shimmer" style={{ height: 9, width: 40, marginBottom: 4, '--sk-base': c.bgMuted, '--sk-shine': c.bgHover }} />
          <div className="sim-shimmer" style={{ height: 28, width: 64, '--sk-base': c.bgMuted, '--sk-shine': c.bgHover }} />
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <div className="sim-shimmer" style={{ height: 9, width: 40, marginBottom: 4, '--sk-base': c.bgMuted, '--sk-shine': c.bgHover }} />
          <div className="sim-shimmer" style={{ height: 28, width: 64, '--sk-base': c.bgMuted, '--sk-shine': c.bgHover }} />
        </div>
      </div>
      <div className="sim-shimmer" style={{ height: 120, width: '100%', borderRadius: radius.md, '--sk-base': c.bgMuted, '--sk-shine': c.bgHover }} />
    </div>
  );
}

// ── Main OutcomePanel ─────────────────────────────────────────────────────────────
export function OutcomePanel({ config, result, activeOutputKeys, setActiveOutputKeys, isPredicting, c, isDark }) {
  const { pct, completing } = useSimProgress(isPredicting);
  // Keep loader visible for 900ms after result arrives so user sees 100% + all ticks
  const [showLoader, setShowLoader] = useState(false);
  useEffect(() => {
    if (isPredicting) { setShowLoader(true); return undefined; }
    if (!completing) return undefined;
    const t = setTimeout(() => setShowLoader(false), 900);
    return () => clearTimeout(t);
  }, [isPredicting, completing]);

  const { outcomes: configOutcomes = [] } = config ?? {};

  // BE sends all supported output keys in result.outputKeys after first predict
  // Fall back to config.outcomes for the picker before any run
  const allOutcomes = useMemo(() => {
    if (result?.outputKeys?.length) {
      // Merge result.outputKeys with config outcomes to get display metadata
      const configMap = Object.fromEntries(configOutcomes.map((o) => [o.dataKey, o]));
      return result.outputKeys
        .map((k) => configMap[k])
        .filter(Boolean);
    }
    return configOutcomes;
  }, [result, configOutcomes]);

  const outcomeMap = useMemo(() => Object.fromEntries(allOutcomes.map((o) => [o.dataKey, o])), [allOutcomes]);

  const displayOutcomes = useMemo(() => activeOutputKeys
    .map((key) => {
      const base = outcomeMap[key];
      if (!base) return null;
      const predicted = result?.outcomes?.find((o) => o.dataKey === key);
      return {
        ...base,
        predictedValue:   predicted?.predictedValue   ?? null,
        timeSeriesValues: predicted?.timeSeriesValues ?? [],
        stabilizeAfter:   predicted?.stabilizeAfter   ?? null,
      };
    })
    .filter(Boolean), [activeOutputKeys, outcomeMap, result]);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius['2xl'], overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding: '14px 16px 12px', borderBottom: `1px solid ${c.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, color: c.textPrimary, flex: 1 }}>Outcome Variables</span>
          {!isPredicting && result && (
            <span style={{
              fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, padding: '2px 8px', borderRadius: radius.lg,
              background: isDark ? 'rgba(74,222,128,0.1)' : 'rgba(22,163,74,0.1)',
              color: successColor(isDark),
              border: `1px solid ${isDark ? 'rgba(74,222,128,0.25)' : 'rgba(22,163,74,0.25)'}`,
            }}>
              Simulation result
            </span>
          )}
          {!isPredicting && !result && (
            <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>Adjust levers and run simulation</span>
          )}
          <OutcomePicker allOutcomes={allOutcomes} activeKeys={activeOutputKeys} setActiveKeys={setActiveOutputKeys} c={c} isDark={isDark} />
        </div>
      </div>

      {/* Cards grid */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px' }}>
        {showLoader ? (
          <SimLoader pct={pct} showingResult={completing} isDark={isDark} c={c} />
        ) : displayOutcomes.length === 0 ? (
          <EmptyState
            icon="analytics"
            title="No outcome variables selected"
            description="Use the Variables button above to select which parameters to track."
          />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {displayOutcomes.map((o) => (
              <OutcomeCard key={o.dataKey} outcome={o} hasRun={!!result} isDark={isDark} c={c} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
